import { useCallback } from 'react'
import {
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_FILE_NAME_LENGTH,
  ATTACHMENT_MIME_ALLOWLIST,
} from '@floorfile/contracts'
import { useCopy } from '@floorfile/i18n'
import { fieldIssuesFromError } from '@/lib/field-issues'
import { attachmentsCopy } from './copy'

/**
 * F32 (NEW_FEATURES_MAINTAINX_PLAN): an empty (0-byte) file uploaded on an
 * equipment's Documents tab failed with "One or more fields are invalid." The
 * server was right to refuse it — `sizeBytes` is `min(1)` — but the upload
 * hooks rebuilt a bare `Error(message)` and threw the field details away, so
 * the screen could never say WHY. Windows' "New → Text Document" makes exactly
 * such a file.
 *
 * Two halves, one vocabulary:
 * - `validateUploadFile` checks a file BEFORE any request, against the same
 *   limits the server enforces (imported from contracts, never copied);
 * - `uploadIssueFromError` reads the same four reasons back out of a server
 *   422's `details[].path`, for anything the client check did not catch.
 * `useUploadErrorMessage` turns either into one localized sentence.
 */
export type UploadIssue = 'EMPTY' | 'TOO_LARGE' | 'NAME_TOO_LONG' | 'TYPE_NOT_ALLOWED'

export class UploadValidationError extends Error {
  /** FF-QA-052: an envelope-style code so `apiErrorMessage` finds a sentence, never the enum. */
  /** The dialog shows this refusal inline; the global toast stays quiet (`providers.tsx`). */
  readonly handledInline = true as const
  constructor(
    readonly issue: UploadIssue,
    readonly mimeType?: string,
    /** `.exe` — what a person recognises; the MIME type is the fallback. */
    readonly extension?: string,
  ) {
    super(`Upload refused: ${issue}`)
    this.name = 'UploadValidationError'
  }
  get code(): `UPLOAD_${UploadIssue}` {
    return `UPLOAD_${this.issue}`
  }
}

/** `setup.exe` → `.exe`; a name with no extension gives nothing. */
export function fileExtension(name: string): string | undefined {
  const ext = /\.([A-Za-z0-9]{1,12})$/.exec(name.trim())?.[1]
  return ext ? `.${ext.toLowerCase()}` : undefined
}

const ALLOWED: ReadonlySet<string> = new Set(ATTACHMENT_MIME_ALLOWLIST)

/** The declared type the server compares — the bare type, as uploads send it. */
export function declaredMimeType(file: { type: string }): string {
  return (file.type || 'application/octet-stream').split(';')[0]?.trim() ?? 'application/octet-stream'
}

export function validateUploadFile(file: { size: number; name: string; type: string }): UploadIssue | null {
  if (file.size <= 0) return 'EMPTY'
  if (file.size > ATTACHMENT_MAX_BYTES) return 'TOO_LARGE'
  if (file.name.length > ATTACHMENT_MAX_FILE_NAME_LENGTH) return 'NAME_TOO_LONG'
  if (!ALLOWED.has(declaredMimeType(file))) return 'TYPE_NOT_ALLOWED'
  return null
}

/** Throws the typed refusal a dialog can name; the hooks call this first. */
export function assertUploadable(file: { size: number; name: string; type: string }): void {
  const issue = validateUploadFile(file)
  if (issue) throw new UploadValidationError(issue, declaredMimeType(file), fileExtension(file.name))
}

export function uploadIssueFromError(error: unknown): { issue: UploadIssue; mimeType?: string | undefined; extension?: string | undefined } | undefined {
  if (error instanceof UploadValidationError) return { issue: error.issue, mimeType: error.mimeType, extension: error.extension }
  for (const { path, code } of fieldIssuesFromError(error)) {
    if (path === 'sizeBytes') return { issue: code === 'FILE_TOO_LARGE' || code === 'too_big' ? 'TOO_LARGE' : 'EMPTY' }
    if (path === 'fileName') return { issue: 'NAME_TOO_LONG' }
    if (path === 'mimeType') return { issue: 'TYPE_NOT_ALLOWED' }
  }
  return undefined
}

/** One sentence for any upload failure: the specific reason; else the
 * error's own message; else the caller's fallback; else "Upload failed." */
export function useUploadErrorMessage(): (error: unknown, fallback?: string) => string {
  const c = useCopy(attachmentsCopy).upload
  return useCallback(
    (error: unknown, fallback?: string) => {
      const found = uploadIssueFromError(error)
      if (found) {
        switch (found.issue) {
          case 'EMPTY':
            return c.empty
          case 'TOO_LARGE':
            return c.tooLarge({ max: String(ATTACHMENT_MAX_BYTES / 1024 / 1024) })
          case 'NAME_TOO_LONG':
            return c.nameTooLong({ max: String(ATTACHMENT_MAX_FILE_NAME_LENGTH) })
          case 'TYPE_NOT_ALLOWED': {
            const type = found.extension ?? found.mimeType
            return type ? c.typeNotAllowed({ type }) : c.typeNotAllowedGeneric
          }
        }
      }
      if (error instanceof Error && error.message) return error.message
      return fallback ?? c.failed
    },
    [c],
  )
}

/** For a file input's `onChange`: the reason this file cannot be uploaded,
 * shown under the control before the user presses Upload. */
export function useUploadFileCheck(): (file: File | undefined) => string | undefined {
  const message = useUploadErrorMessage()
  return useCallback(
    (file: File | undefined) => {
      if (!file) return undefined
      const issue = validateUploadFile(file)
      return issue ? message(new UploadValidationError(issue, declaredMimeType(file), fileExtension(file.name))) : undefined
    },
    [message],
  )
}
